import React from 'react'
import { Progress, Spinner, Alert, Button } from '@/components/ui-compat'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome'
import {
  faCheckCircle,
  faExclamationTriangle,
  faRedo,
} from '@fortawesome/free-solid-svg-icons'
import { Job, JobStatus } from '../../hooks/useJobProgress'

interface JobProgressIndicatorProps {
  job: Job | null
  loading?: boolean
  showSteps?: boolean
  showPercentage?: boolean
  onRetry?: () => void
  className?: string
}

const JobProgressIndicator: React.FC<JobProgressIndicatorProps> = ({
  job,
  loading = false,
  showSteps = true,
  showPercentage = true,
  onRetry,
  className = '',
}) => {
  const { t } = useTranslation()

  // Loading state (before first job data arrives)
  if (loading && !job) {
    return (
      <div className={`text-center py-3 ${className}`}>
        <Spinner color="primary" size="sm" className="mr-2" />
        <span className="text-muted-foreground">{t('Job_status_loading', 'Loading...')}</span>
      </div>
    )
  }

  if (!job) {
    return null
  }

  const { status, progress, currentStep, currentStepNum, totalSteps, error } = job

  // Completed state
  if (status === 'COMPLETED') {
    return (
      <div className={`text-center py-3 ${className}`}>
        <Icon icon={faCheckCircle as any} className="text-[hsl(var(--success))] mr-2" style={{ fontSize: '1.5rem' }} />
        <span className="font-bold text-[hsl(var(--success))]">{t('Job_status_completed', 'Completed')}</span>
      </div>
    )
  }

  // Failed state
  if (status === 'FAILED' || status === 'CANCELLED') {
    return (
      <div className={className}>
        <Alert color="danger" className="mb-0 flex items-center justify-between">
          <div>
            <Icon icon={faExclamationTriangle as any} className="mr-2" />
            <span>{error || t('Job_status_failed', 'Failed')}</span>
          </div>
          {onRetry && (
            <Button color="danger" size="sm" outline onClick={onRetry}>
              <Icon icon={faRedo as any} className="mr-1" />
              {t('Job_retry', 'Retry')}
            </Button>
          )}
        </Alert>
      </div>
    )
  }

  // Processing/Pending state
  return (
    <div className={className}>
      <div className="flex justify-between items-center mb-2">
        <div className="flex items-center">
          <Spinner size="sm" color="primary" className="mr-2" />
          <span className="font-bold">
            {currentStep || t('Job_status_processing', 'Processing...')}
          </span>
        </div>
        {showPercentage && (
          <span className="text-muted-foreground small">{progress}%</span>
        )}
      </div>

      <Progress
        value={progress}
        color="primary"
        animated
        striped
        style={{ height: 8 }}
      />

      {showSteps && currentStepNum && totalSteps && (
        <p className="text-muted-foreground small mt-2 mb-0">
          {t('Job_step_progress', 'Step {{current}} of {{total}}', {
            current: currentStepNum,
            total: totalSteps,
          })}
        </p>
      )}
    </div>
  )
}

export default JobProgressIndicator
